/**
 * Mock WebSocket using StorageEvent API
 * Simulates real-time communication across browser tabs
 */

type WebSocketEventType =
    | 'new_message'
    | 'typing_start'
    | 'typing_stop'
    | 'user_online'
    | 'user_offline'
    | 'message_deleted'
    | 'message_edited';

interface WebSocketEvent {
    type: WebSocketEventType;
    data: any;
    timestamp: string;
}

type EventCallback = (event: WebSocketEvent) => void;

class ChatWebSocketMock {
    private listeners: EventCallback[] = [];
    private heartbeatInterval: NodeJS.Timeout | null = null;
    private userId: string | null = null;

    constructor() {
        if (typeof window !== 'undefined') {
            // Listen to storage events from other tabs
            window.addEventListener('storage', this.handleStorageChange.bind(this));
        }
    }

    /**
     * Subscribe to WebSocket events
     */
    subscribe(callback: EventCallback) {
        this.listeners.push(callback);
        return () => {
            this.listeners = this.listeners.filter(cb => cb !== callback);
        };
    }

    /**
     * Broadcast event to all tabs
     */
    broadcast(type: WebSocketEventType, data: any) {
        if (typeof window === 'undefined') return;

        const event: WebSocketEvent = {
            type,
            data,
            timestamp: new Date().toISOString(),
        };

        // Trigger storage event by writing to localStorage
        const eventKey = `chat_event_${Date.now()}_${Math.random()}`;
        localStorage.setItem(eventKey, JSON.stringify(event));

        // Clean up old events
        setTimeout(() => {
            localStorage.removeItem(eventKey);
        }, 1000);

        // Also notify current tab
        this.notifyListeners(event);
    }

    /**
     * Handle storage changes from other tabs
     */
    private handleStorageChange(e: StorageEvent) {
        if (!e.key?.startsWith('chat_event_')) return;
        if (!e.newValue) return;

        try {
            const event: WebSocketEvent = JSON.parse(e.newValue);
            this.notifyListeners(event);
        } catch (error) {
            console.error('Failed to parse WebSocket event:', error);
        }
    }

    /**
     * Notify all listeners
     */
    private notifyListeners(event: WebSocketEvent) {
        this.listeners.forEach(callback => {
            try {
                callback(event);
            } catch (error) {
                console.error('Listener error:', error);
            }
        });
    }

    /**
     * Start heartbeat for online status
     */
    startHeartbeat(userId: string) {
        this.userId = userId;
        this.updateOnlineStatus(userId, 'online');

        // Send heartbeat every 10 seconds
        this.heartbeatInterval = setInterval(() => {
            this.updateOnlineStatus(userId, 'online');
        }, 10000);

        // Update status on page unload
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                this.updateOnlineStatus(userId, 'offline');
            });
        }
    }

    /**
     * Stop heartbeat
     */
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }

        if (this.userId) {
            this.updateOnlineStatus(this.userId, 'offline');
        }
    }

    /**
     * Update user online status
     */
    private updateOnlineStatus(userId: string, status: 'online' | 'offline') {
        const statusKey = `user_status_${userId}`;
        const statusData = {
            userId,
            status,
            lastSeen: new Date().toISOString(),
        };

        localStorage.setItem(statusKey, JSON.stringify(statusData));
        this.broadcast('user_online', statusData);
    }

    /**
     * Get all online users
     */
    getOnlineUsers(): string[] {
        if (typeof window === 'undefined') return [];

        const users: string[] = [];
        const now = Date.now();
        const timeout = 30000; // 30 seconds timeout

        // Check all user status keys
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key?.startsWith('user_status_')) continue;

            try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                const lastSeen = new Date(data.lastSeen).getTime();

                // Consider online if heartbeat within 30 seconds
                if (now - lastSeen < timeout) {
                    users.push(data.userId);
                }
            } catch (error) {
                // Ignore invalid data
            }
        }

        return users;
    }

    /**
     * Send typing indicator
     */
    sendTyping(userId: string, userName: string, channelId: string) {
        this.broadcast('typing_start', {
            userId,
            userName,
            channelId,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Stop typing indicator
     */
    stopTyping(userId: string, channelId: string) {
        this.broadcast('typing_stop', {
            userId,
            channelId,
        });
    }

    /**
     * Cleanup old data
     */
    cleanup() {
        if (typeof window === 'undefined') return;

        const now = Date.now();
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours

        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (!key?.startsWith('chat_')) continue;

            try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.timestamp) {
                    const age = now - new Date(data.timestamp).getTime();
                    if (age > maxAge) {
                        localStorage.removeItem(key);
                    }
                }
            } catch (error) {
                // Remove invalid data
                if (key) localStorage.removeItem(key);
            }
        }
    }
}

// Export singleton instance
export const chatWebSocket = new ChatWebSocketMock();
